import { useState } from "react";
import { LocalizedLink } from "@/i18n/LocalizedLink";
import { toast } from "sonner";
import { KeyRound, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

export default function ForgotPassword() {
  const { t } = useLang();
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/users/forgot-password", { identifier });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[80vh] grid place-items-center p-6 relative" data-testid="forgot-page">
      <div className="aurora opacity-50" />
      <div className="relative w-full max-w-md">
        <LocalizedLink to="/signin" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-4 h-4 rtl-flip" /> {t("nav.sign_in")}
        </LocalizedLink>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-brand to-brand-cyan glow-blue mb-4">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">{t("auth.forgot_title")}</h1>
          <p className="text-sm text-zinc-400">{t("auth.forgot_sub")}</p>
        </div>

        {sent ? (
          <div className="bg-[#0c0c0c] border border-brand-green/30 rounded-xl p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-brand-green mx-auto mb-3" />
            <p className="text-zinc-300">{t("auth.forgot_sent")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2 block">
                {t("auth.identifier")}
              </label>
              <input
                required value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="w-full bg-[#080808] border border-white/10 rounded-lg px-4 py-3 focus:border-brand outline-none transition-colors"
                data-testid="forgot-identifier"
              />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3 rounded-lg glow-blue transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="forgot-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {t("auth.send_reset")}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
